import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { useHistory } from 'react-router-dom';
import {
  suggestionsSectionsSelector,
  suggestionsLoadingSelector,
  suggestionsFetchedSelector,
  fetchSuggestions
} from '../../redux/features/suggestions/suggestionsSlice';
import { buildJobUrl } from '../../helpers/urlHelpers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  section: {
    marginBottom: theme.spacing(4)
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  jobCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
      borderColor: theme.palette.primary.main
    }
  },
  cardContent: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  jobTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4,
    minHeight: '2.8em'
  },
  companyName: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  icon: {
    fontSize: '1rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6)
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  }
}));

const categoryLabels = {
  dev: 'Development',
  design: 'Design',
  marketing: 'Marketing',
  sales: 'Sales',
  customer_support: 'Customer Support',
  finance: 'Finance',
  operations: 'Operations',
  hr: 'Human Resources',
  legal: 'Legal',
  other: 'Other'
};

const Suggestions = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const sections = useSelector(suggestionsSectionsSelector);
  const loading = useSelector(suggestionsLoadingSelector);
  const fetched = useSelector(suggestionsFetchedSelector);

  useEffect(() => {
    if (!loading && !fetched) {
      dispatch(fetchSuggestions());
    }
  }, [dispatch, fetched, loading]);

  const handleJobClick = (jobResult) => {
    const job = jobResult.job;
    const company = jobResult.company;
    if (!job || !company) return;
    history.push(buildJobUrl(job.id, job.title, company.name));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatJobType = (type) => {
    if (!type) return 'Role';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!sections.length) {
    return (
      <Box className={classes.root}>
        <Box className={classes.emptyState}>
          <Typography variant="h5" gutterBottom>
            No suggestions yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Apply to a few roles and we will surface more jobs you may like.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {sections.map((section) => (
        <Box key={section.category} className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Suggestions in {categoryLabels[section.category] || section.category}
          </Typography>
          <Typography variant="body2" className={classes.sectionSubtitle}>
            Based on your recent applications, here are new roles to explore.
          </Typography>
          <Grid container spacing={3}>
            {section.jobs.map((jobResult) => (
              <Grid item xs={12} sm={6} md={4} key={jobResult.job.id}>
                <Card className={classes.jobCard}>
                  <CardActionArea
                    onClick={() => handleJobClick(jobResult)}
                    style={{ height: '100%' }}>
                    <CardContent className={classes.cardContent}>
                      <Typography className={classes.jobTitle}>
                        {jobResult.job.title}
                      </Typography>
                      <Typography className={classes.companyName}>
                        {jobResult.company?.name || 'Company'}
                      </Typography>
                      <Box className={classes.detailsRow}>
                        <WorkOutlineIcon className={classes.icon} />
                        <span>{formatJobType(jobResult.job.type)}</span>
                      </Box>
                      <Box className={classes.detailsRow}>
                        <LocationOnIcon className={classes.icon} />
                        <span>
                          {jobResult.job.regionalRestrictions || 'Remote'}
                        </span>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

Suggestions.propTypes = {
  sections: PropTypes.array
};

export default Suggestions;
